import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Upload, Download, Trash2, X, CheckCircle, AlertCircle, 
  Loader2, FileText, Info, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePickerInput from '@/components/DatePickerInput';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Google Analytics SVG Icon
const GoogleAnalyticsIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none">
    <path d="M22.84 2.9982V20.9982C22.84 22.1028 21.9446 22.9982 20.84 22.9982H18.84V7.9982C18.84 6.8936 17.9446 5.9982 16.84 5.9982H14.84V2.9982C14.84 1.8936 15.7354 0.9982 16.84 0.9982H20.84C21.9446 0.9982 22.84 1.8936 22.84 2.9982Z" fill="#F9AB00"/>
    <path d="M14.84 9.9982V20.9982C14.84 22.1028 13.9446 22.9982 12.84 22.9982H10.84V14.9982C10.84 13.8936 9.9446 12.9982 8.84 12.9982H6.84V9.9982C6.84 8.8936 7.7354 7.9982 8.84 7.9982H12.84C13.9446 7.9982 14.84 8.8936 14.84 9.9982Z" fill="#E37400"/>
    <ellipse cx="3.84" cy="19.9982" rx="3" ry="3" fill="#E37400"/>
  </svg>
);

const ImportExportSection = ({ websiteId, token }) => {
  const confirm = useConfirm();
  // State
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gaConnected, setGaConnected] = useState(false);
  // Google account email currently linked to GA (for the "Linked Google account"
  // indicator in the GA Import modal, matches the Search Console connected UI).
  const [gaEmail, setGaEmail] = useState(null);
  const [gaProperties, setGaProperties] = useState([]);
  const [showGAModal, setShowGAModal] = useState(false);
  const [showCSVModal, setShowCSVModal] = useState(false);
  const [showPlausibleModal, setShowPlausibleModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [importing, setImporting] = useState(false);
  const [exportStatus, setExportStatus] = useState(null);
  const [exportId, setExportId] = useState(null);
  
  // CSV states
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [pendingCsvImportId, setPendingCsvImportId] = useState(null);
  
  // Plausible import states
  const [plausibleFiles, setPlausibleFiles] = useState([]);
  const [plausiblePreview, setPlausiblePreview] = useState(null);
  const [plausibleUploading, setPlausibleUploading] = useState(false);
  const [pendingPlausibleImportId, setPendingPlausibleImportId] = useState(null);
  
  // Drag and drop
  const [dragActive, setDragActive] = useState(false);

  // Fetch imports list
  const fetchImports = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/imports`, {
        params: { website_id: websiteId, token }
      });
      setImports(res.data.imports || []);
    } catch (err) {
      console.error('Error fetching imports:', err);
    } finally {
      setLoading(false);
    }
  }, [websiteId, token]);

  // Check GA connection status
  const checkGAStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/imports/google/status`, {
        params: { website_id: websiteId, token }
      });
      setGaConnected(res.data.connected);
      setGaEmail(res.data.google_email || null);
    } catch (err) {
      console.error('Error checking GA status:', err);
    }
  }, [websiteId, token]);

  useEffect(() => {
    fetchImports();
    checkGAStatus();
  }, [fetchImports, checkGAStatus]);

  // Poll for import status updates when any imports are in progress
  useEffect(() => {
    const hasActiveImports = imports.some(
      imp => imp.status === 'in_progress' || imp.status === 'pending'
    );
    
    if (!hasActiveImports) return;
    
    const interval = setInterval(() => {
      fetchImports();
    }, 3000);
    
    return () => clearInterval(interval);
  }, [imports, fetchImports]);

  // Set default date range (last year)
  useEffect(() => {
    const today = new Date();
    const lastYear = new Date(today);
    lastYear.setFullYear(lastYear.getFullYear() - 1);
    
    setDateEnd(today.toISOString().split('T')[0]);
    setDateStart(lastYear.toISOString().split('T')[0]);
  }, []);

  // ============== GOOGLE ANALYTICS ==============
  
  const handleGAConnect = async () => {
    try {
      // Get the OAuth URL from backend (redirect_uri is now handled server-side)
      const res = await axios.get(`${API}/imports/google/auth-url`, {
        params: { 
          website_id: websiteId, 
          token 
        }
      });
      
      // Redirect to Google OAuth
      window.location.href = res.data.auth_url;
    } catch (err) {
      if (err.response?.data?.detail?.includes('not configured')) {
        toast.error('Google Analytics integration requires configuration. Please contact support.');
      } else {
        toast.error('Failed to start Google authentication');
      }
    }
  };

  // Handle OAuth success/error from URL params (after redirect back from Google)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const gaConnectedParam = urlParams.get('ga_connected');
    const errorParam = urlParams.get('error');
    
    if (gaConnectedParam === 'true') {
      toast.success('Google Analytics connected successfully!');
      setGaConnected(true);
      fetchGAProperties();
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (errorParam) {
      if (errorParam === 'oauth_failed') {
        toast.error('Failed to connect Google Analytics. Please try again.');
      } else if (errorParam === 'invalid_oauth_state') {
        toast.error('Invalid OAuth state. Please try again.');
      } else if (errorParam === 'oauth_state_expired') {
        toast.error('OAuth session expired. Please try again.');
      }
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const fetchGAProperties = async () => {
    try {
      const res = await axios.get(`${API}/imports/google/properties`, {
        params: { website_id: websiteId, token }
      });
      setGaProperties(res.data.properties || []);
    } catch (err) {
      toast.error('Failed to fetch GA properties');
    }
  };

  const handleGAImport = async () => {
    if (!selectedProperty || !dateStart || !dateEnd) {
      toast.error('Please select a property and date range');
      return;
    }

    setImporting(true);
    try {
      await axios.post(`${API}/imports/google/import`, null, {
        params: {
          website_id: websiteId,
          property_id: selectedProperty,
          date_start: dateStart,
          date_end: dateEnd,
          token
        }
      });
      
      toast.success('Import started! This may take a few minutes.');
      setShowGAModal(false);
      fetchImports();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start import');
    } finally {
      setImporting(false);
    }
  };

  const disconnectGA = async () => {
    try {
      await axios.delete(`${API}/imports/google/disconnect`, {
        params: { website_id: websiteId, token }
      });
      setGaConnected(false);
      setGaEmail(null);
      setGaProperties([]);
      toast.success('Google Analytics disconnected');
    } catch (err) {
      toast.error('Failed to disconnect');
    }
  };

  // ============== CSV IMPORT ==============

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = async (file) => {
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    
    setCsvFile(file);
    setCsvUploading(true);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await axios.post(
        `${API}/imports/csv/upload?website_id=${websiteId}&token=${token}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setCsvPreview(res.data);
      setPendingCsvImportId(res.data.import_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process CSV');
      setCsvFile(null);
    } finally {
      setCsvUploading(false);
    }
  };

  const confirmCSVImport = async () => {
    if (!pendingCsvImportId) return;
    
    setImporting(true);
    try {
      await axios.post(`${API}/imports/csv/confirm/${pendingCsvImportId}`, null, {
        params: { token }
      });
      
      toast.success('CSV import started!');
      setShowCSVModal(false);
      setCsvFile(null);
      setCsvPreview(null);
      setPendingCsvImportId(null);
      fetchImports();
    } catch (err) {
      toast.error('Failed to start import');
    } finally {
      setImporting(false);
    }
  };

  const cancelCSVUpload = () => {
    setCsvFile(null);
    setCsvPreview(null);
    setPendingCsvImportId(null);
  };

  // ============== PLAUSIBLE CSV IMPORT ==============

  const handlePlausibleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handlePlausibleFileSelect(Array.from(e.dataTransfer.files));
    }
  };

  const handlePlausibleFileSelect = async (files) => {
    // Filter valid files
    const validFiles = files.filter(f => 
      f.name.endsWith('.csv') || f.name.endsWith('.zip')
    );
    
    if (validFiles.length === 0) {
      toast.error('Please upload CSV or ZIP files');
      return;
    }
    
    setPlausibleFiles(validFiles);
    setPlausibleUploading(true);
    
    const formData = new FormData();
    validFiles.forEach(file => {
      formData.append('files', file);
    });
    
    try {
      const res = await axios.post(
        `${API}/imports/plausible/upload?website_id=${websiteId}&token=${token}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setPlausiblePreview(res.data);
      setPendingPlausibleImportId(res.data.import_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process files');
      setPlausibleFiles([]);
    } finally {
      setPlausibleUploading(false);
    }
  };

  const confirmPlausibleImport = async () => {
    if (!pendingPlausibleImportId) return;
    
    setImporting(true);
    try {
      await axios.post(`${API}/imports/plausible/confirm/${pendingPlausibleImportId}`, null, {
        params: { token }
      });
      
      toast.success('Plausible import started!');
      setShowPlausibleModal(false);
      setPlausibleFiles([]);
      setPlausiblePreview(null);
      setPendingPlausibleImportId(null);
      fetchImports();
    } catch (err) {
      toast.error('Failed to start import');
    } finally {
      setImporting(false);
    }
  };

  const cancelPlausibleUpload = () => {
    setPlausibleFiles([]);
    setPlausiblePreview(null);
    setPendingPlausibleImportId(null);
  };

  // ============== DELETE IMPORT ==============

  const deleteImport = async (importId) => {
    const ok = await confirm({
      title: 'Delete this import?',
      description: 'The imported pageviews and events from this dataset will be permanently removed from your analytics.',
      confirmLabel: 'Delete import',
    });
    if (!ok) return;

    try {
      await axios.delete(`${API}/imports/${importId}`, {
        params: { token }
      });
      toast.success('Import deleted');
      fetchImports();
    } catch (err) {
      toast.error('Failed to delete import');
    }
  };

  // ============== EXPORT ==============

  const startExport = async () => {
    setExportStatus('preparing');
    try {
      const res = await axios.post(`${API}/imports/export/prepare`, null, {
        params: { website_id: websiteId, token }
      });
      setExportId(res.data.export_id);
      pollExportStatus(res.data.export_id);
    } catch (err) {
      toast.error('Failed to start export');
      setExportStatus(null);
    }
  };

  const pollExportStatus = async (expId) => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/imports/export/${expId}/status`, {
          params: { token }
        });
        
        if (res.data.status === 'ready') {
          clearInterval(interval);
          setExportStatus('ready');
        } else if (res.data.status === 'failed') {
          clearInterval(interval);
          setExportStatus('failed');
          toast.error('Export failed');
        }
      } catch (err) {
        clearInterval(interval);
        setExportStatus('failed');
      }
    }, 2000);
  };

  const downloadExport = () => {
    if (!exportId) return;
    window.open(`${API}/imports/export/${exportId}/download?token=${token}`, '_blank');
  };

  // ============== RENDER ==============

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toLocaleString();
  };

  return (
    <div className="settings-content">
      {/* Import Section */}
      <div className="settings-section mb-8">
        <div className="section-header flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="section-title">Import data</h2>
            <button className="text-gray-400 hover:text-gray-600">
              <Info className="h-4 w-4" />
            </button>
          </div>
        </div>
        
        <p className="text-sm text-gray-600 mb-4">
          Import data from external sources. Up to 5 imports are allowed at a time.
        </p>

        {/* Import Buttons */}
        <div className="flex flex-wrap gap-3 mb-6">
          <Button
            variant="outline"
            onClick={() => {
              if (gaConnected) {
                fetchGAProperties();
                setShowGAModal(true);
              } else {
                handleGAConnect();
              }
            }}
            className="flex items-center gap-2"
            data-testid="import-google-analytics-btn"
          >
            <span className="text-sm">Import from</span>
            <GoogleAnalyticsIcon />
            <span className="text-xs text-gray-500">Google Analytics</span>
          </Button>
          
          <Button
            onClick={() => setShowPlausibleModal(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="import-plausible-btn"
          >
            Import from Plausible
          </Button>
        </div>

        {/* Imports Table */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : imports.length > 0 ? (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Import</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date Range</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Pageviews</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {imports.map((imp) => (
                  <tr key={imp.id} className="hover:bg-gray-50" data-testid={`import-row-${imp.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {imp.source === 'google_analytics' ? (
                          <>
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            <span className="text-sm text-gray-900">Google Analytics</span>
                          </>
                        ) : imp.source === 'plausible_csv' ? (
                          <>
                            <CheckCircle className="w-4 h-4 text-purple-500" />
                            <span className="text-sm text-gray-900">Plausible CSV</span>
                          </>
                        ) : (
                          <>
                            <FileText className="w-4 h-4 text-blue-500" />
                            <span className="text-sm text-gray-900">CSV</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {formatDate(imp.date_range_start)} - {formatDate(imp.date_range_end)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                      {formatNumber(imp.total_pageviews)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {imp.status === 'completed' && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Completed
                        </span>
                      )}
                      {imp.status === 'in_progress' && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Importing
                        </span>
                      )}
                      {imp.status === 'pending' && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Pending
                        </span>
                      )}
                      {imp.status === 'failed' && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800" title={imp.error_message}>
                          Failed
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => deleteImport(imp.id)}
                        className="text-red-500 hover:text-red-700 p-1"
                        data-testid={`delete-import-${imp.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="border border-dashed border-gray-300 rounded-lg p-8 text-center">
            <p className="text-gray-500">No imports yet</p>
          </div>
        )}
      </div>

      {/* Export Section */}
      <div className="settings-section">
        <div className="section-header flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="section-title">Export data</h2>
            <button className="text-gray-400 hover:text-gray-600">
              <Info className="h-4 w-4" />
            </button>
          </div>
        </div>
        
        <p className="text-sm text-gray-600 mb-4">
          Export all your data into CSV format.
        </p>

        <div className="border border-gray-200 rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-4">
            Prepare your data for download by clicking the button below. When that's done, a Zip file that you can download will appear.
          </p>

          {!exportStatus && (
            <Button
              onClick={startExport}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="prepare-export-btn"
            >
              Prepare download
            </Button>
          )}

          {exportStatus === 'preparing' && (
            <Button disabled className="bg-gray-400 text-white">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Preparing...
            </Button>
          )}

          {exportStatus === 'ready' && (
            <div className="flex items-center gap-3">
              <Button
                onClick={downloadExport}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="download-export-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download ZIP
              </Button>
              <span className="text-sm text-green-600 flex items-center gap-1">
                <CheckCircle className="w-4 h-4" />
                Ready to download
              </span>
            </div>
          )}

          {exportStatus === 'failed' && (
            <div className="flex items-center gap-3">
              <Button
                onClick={startExport}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                Retry
              </Button>
              <span className="text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                Export failed
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Google Analytics Modal */}
      {showGAModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Import from Google Analytics</h3>
              <button onClick={() => setShowGAModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {gaConnected ? (
              <>
                {/* Linked Google account banner — matches the Search Console
                    integration UI so the user can see exactly which Google
                    account is connected, with a one-click Disconnect. */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg mb-4">
                  <div className="flex items-center gap-2 min-w-0">
                    <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                    <span className="text-sm text-gray-700">Linked Google account</span>
                    <span className="text-sm font-medium text-gray-900 truncate" title={gaEmail || ''}>
                      {gaEmail || 'Connected'}
                    </span>
                  </div>
                  <button
                    onClick={disconnectGA}
                    className="text-sm text-red-600 hover:text-red-700 font-medium shrink-0"
                  >
                    Disconnect
                  </button>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select GA4 Property
                  </label>
                  <select
                    value={selectedProperty}
                    onChange={(e) => setSelectedProperty(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg p-2"
                  >
                    <option value="">Select a property...</option>
                    {gaProperties.map((prop) => (
                      <option key={prop.property_id} value={prop.property_id}>
                        {prop.display_name} ({prop.account_name})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date
                    </label>
                    <DatePickerInput
                      value={dateStart}
                      onChange={setDateStart}
                      placeholder="Start date"
                      max={dateEnd || undefined}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date
                    </label>
                    <DatePickerInput
                      value={dateEnd}
                      onChange={setDateEnd}
                      placeholder="End date"
                      min={dateStart || undefined}
                    />
                  </div>
                </div>

                {/* Disconnect lives in the "Linked Google account" banner at
                    the top of the modal — keeps the action grouped with the
                    account info and consistent with the Search Console UI. */}
                <div className="flex gap-3 justify-end">
                  <Button
                    onClick={handleGAImport}
                    disabled={importing || !selectedProperty}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  >
                    {importing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Starting...
                      </>
                    ) : (
                      'Start Import'
                    )}
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <p className="text-gray-600 mb-4">Connect your Google Analytics account to import data.</p>
                <Button onClick={handleGAConnect} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                  Connect Google Analytics
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CSV Upload Modal */}
      {showCSVModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Import from CSV</h3>
              <button onClick={() => { setShowCSVModal(false); cancelCSVUpload(); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {!csvPreview ? (
              <>
                {/* Drop Zone */}
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    dragActive ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  {csvUploading ? (
                    <div className="flex flex-col items-center">
                      <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mb-2" />
                      <p className="text-gray-600">Processing CSV...</p>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600 mb-2">Drag and drop your CSV file here, or</p>
                      <label className="inline-block">
                        <span className="text-indigo-600 hover:text-indigo-700 cursor-pointer font-medium">
                          browse to upload
                        </span>
                        <input
                          type="file"
                          accept=".csv"
                          onChange={(e) => e.target.files[0] && handleFileSelect(e.target.files[0])}
                          className="hidden"
                        />
                      </label>
                    </>
                  )}
                </div>

                {/* CSV Format Info */}
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Required CSV Format</h4>
                  <p className="text-xs text-gray-600 mb-2">Your CSV must include these headers:</p>
                  <code className="text-xs bg-gray-200 px-2 py-1 rounded">date, page_path, pageviews</code>
                  <p className="text-xs text-gray-500 mt-2">
                    Optional: sessions, country, device, source, medium
                  </p>
                </div>
              </>
            ) : (
              <>
                {/* Preview */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Preview ({csvPreview.total_rows} rows)</span>
                    <span className="text-sm text-gray-500">
                      {csvPreview.date_range?.start} - {csvPreview.date_range?.end}
                    </span>
                  </div>
                  
                  <div className="border border-gray-200 rounded-lg overflow-x-auto max-h-64">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          {csvPreview.headers?.map((header) => (
                            <th key={header} className="px-3 py-2 text-left font-medium text-gray-600">
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {csvPreview.preview?.map((row, idx) => (
                          <tr key={idx}>
                            {csvPreview.headers?.map((header) => (
                              <td key={header} className="px-3 py-2 text-gray-700">
                                {row[header]}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Total pageviews: <span className="font-medium">{formatNumber(csvPreview.total_pageviews)}</span>
                  </div>
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={cancelCSVUpload}>
                      Cancel
                    </Button>
                    <Button
                      onClick={confirmCSVImport}
                      disabled={importing}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    >
                      {importing ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Importing...
                        </>
                      ) : (
                        'Confirm Import'
                      )}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Plausible CSV Upload Modal */}
      {showPlausibleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Import from Plausible</h3>
              <button onClick={() => { setShowPlausibleModal(false); cancelPlausibleUpload(); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {!plausiblePreview ? (
              <>
                {/* Drop Zone */}
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    dragActive ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handlePlausibleDrop}
                >
                  {plausibleUploading ? (
                    <div className="flex flex-col items-center">
                      <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mb-2" />
                      <p className="text-gray-600">Processing files...</p>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600 mb-2">Drag and drop your Plausible export files here, or</p>
                      <label className="inline-block">
                        <span className="text-indigo-600 hover:text-indigo-700 cursor-pointer font-medium">
                          browse to upload
                        </span>
                        <input
                          type="file"
                          accept=".csv,.zip"
                          multiple
                          onChange={(e) => e.target.files.length > 0 && handlePlausibleFileSelect(Array.from(e.target.files))}
                          className="hidden"
                          data-testid="plausible-file-input"
                        />
                      </label>
                    </>
                  )}
                </div>

                {/* Plausible Format Info */}
                <div className="mt-4 p-4 bg-purple-50 rounded-lg">
                  <h4 className="text-sm font-medium text-purple-900 mb-2">Supported Plausible Export Files</h4>
                  <p className="text-xs text-purple-700 mb-3">
                    Upload a ZIP file or multiple CSV files from your Plausible export:
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-xs text-purple-600">
                    <div>• imported_visitors.csv</div>
                    <div>• imported_pages.csv</div>
                    <div>• imported_entry_pages.csv</div>
                    <div>• imported_exit_pages.csv</div>
                    <div>• imported_sources.csv</div>
                    <div>• imported_locations.csv</div>
                    <div>• imported_devices.csv</div>
                    <div>• imported_browsers.csv</div>
                    <div>• imported_operating_systems.csv</div>
                    <div>• imported_custom_events.csv</div>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Preview */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      {plausiblePreview.files?.length} file(s) detected ({plausiblePreview.total_rows} total rows)
                    </span>
                    <span className="text-sm text-gray-500">
                      {plausiblePreview.date_range?.start} - {plausiblePreview.date_range?.end}
                    </span>
                  </div>
                  
                  <div className="space-y-3 max-h-80 overflow-y-auto">
                    {plausiblePreview.files?.map((file, idx) => (
                      <div key={idx} className="border border-gray-200 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-purple-500" />
                            <span className="text-sm font-medium text-gray-800">{file.filename}</span>
                          </div>
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            {file.type} • {file.row_count} rows
                          </span>
                        </div>
                        {file.sample_rows?.length > 0 && (
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead className="bg-gray-50">
                                <tr>
                                  {Object.keys(file.sample_rows[0]).slice(0, 5).map((key) => (
                                    <th key={key} className="px-2 py-1 text-left font-medium text-gray-600">
                                      {key}
                                    </th>
                                  ))}
                                  {Object.keys(file.sample_rows[0]).length > 5 && (
                                    <th className="px-2 py-1 text-left font-medium text-gray-400">...</th>
                                  )}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {file.sample_rows.slice(0, 2).map((row, rowIdx) => (
                                  <tr key={rowIdx}>
                                    {Object.keys(row).slice(0, 5).map((key) => (
                                      <td key={key} className="px-2 py-1 text-gray-700 truncate max-w-[100px]">
                                        {row[key]}
                                      </td>
                                    ))}
                                    {Object.keys(row).length > 5 && (
                                      <td className="px-2 py-1 text-gray-400">...</td>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div className="text-sm text-gray-600">
                    Ready to import <span className="font-medium">{plausiblePreview.total_rows}</span> rows
                  </div>
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={cancelPlausibleUpload}>
                      Cancel
                    </Button>
                    <Button
                      onClick={confirmPlausibleImport}
                      disabled={importing}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white"
                      data-testid="confirm-plausible-import-btn"
                    >
                      {importing ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Importing...
                        </>
                      ) : (
                        'Confirm Import'
                      )}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportExportSection;
